import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { VariantColor } from '@repo/toolkit/colors'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { DeleteIcon, FilterIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getCenterTripBookingsTrips } from '../../../api/centers/get-center-tripbookings-trips'
import { TripTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum'
import {
  TripStepTypeEnum,
  tripStepTypeMapping
} from '../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { TripBasic } from '../../../lib/types/models/trip/trip'

export type CenterTripFiltersType = {
  directionalities: TripStepTypeEnum[]
  tripIds: number[]
}

type CenterTripsFilterProps = {
  centerId: number
  filters: CenterTripFiltersType
  disabled?: boolean
  onChange?: (filters: CenterTripFiltersType) => void
}

type FilterKeys = keyof CenterTripFiltersType

const tripTypeVariant: Record<TripTypeEnum, { default: VariantColor; active: VariantColor }> = {
  [TripTypeEnum.PRIMARY]: {
    default: 'transparent',
    active: 'warning'
  },
  [TripTypeEnum.SECONDARY]: {
    default: 'grey',
    active: 'primaryLight'
  }
}

export function CenterTripsFilter(props: CenterTripsFilterProps) {
  const { centerId, filters, disabled, onChange } = props

  const { t } = useTranslation()

  const [mainTrips, setMainTrips] = useState<TripBasic[]>([])
  const [secondaryTrips, setSecondaryTrips] = useState<TripBasic[]>([])

  const { data: trips } = useQuery({
    queryKey: ['center-trip-bookings-trips', centerId],
    queryFn: () => getCenterTripBookingsTrips(centerId)
  })

  function setFilters(key: FilterKeys, value: CenterTripFiltersType[FilterKeys]) {
    onChange?.({ ...filters, [key]: value })
  }

  function TripBadge({ trips, type }: { trips: TripBasic[]; type: TripTypeEnum }) {
    const variant = tripTypeVariant[type]

    return (
      <div className={'flex flex-wrap gap-2 w-full'}>
        {trips.map((t) => (
          <Badge
            key={t.id}
            text={TripProperties.getTripLabel(t)}
            variant={
              disabled ? 'grey' : filters.tripIds.includes(t.id) ? variant.active : variant.default
            }
            className={'cursor-pointer'}
            onClick={() => {
              if (disabled) return

              setFilters(
                'tripIds',
                filters.tripIds.includes(t.id)
                  ? filters.tripIds.filter((id) => id !== t.id)
                  : [...filters.tripIds, t.id]
              )
            }}
          />
        ))}
      </div>
    )
  }

  useEffect(() => {
    if (!trips) return

    setMainTrips(trips.filter((trip) => trip.type === TripTypeEnum.PRIMARY))
    setSecondaryTrips(trips.filter((trip) => trip.type === TripTypeEnum.SECONDARY))
  }, [trips, disabled])

  return (
    <div className={'border border-grey-light rounded-lg'}>
      {/* Header */}
      <div className="bg-white-hover rounded-t-lg flex items-center justify-between flex-row gap-2 p-2">
        <div className="flex items-center flex-row gap-2">
          <div className="w-7 h-7 flex justify-center items-center bg-primary-light rounded-sm p-1">
            <FilterIcon color={tailwindColors.primary.DEFAULT} />
          </div>

          <Typography variant={'tag'}>Filtrer par</Typography>
        </div>

        <Button
          icon={DeleteIcon}
          variant={'destructive'}
          label={'Effacer les filtres'}
          disabled={disabled}
          onClick={() => onChange?.({ directionalities: [], tripIds: [] })}
        />
      </div>

      {/* Body */}
      <div className={'flex flex-col gap-2 p-4'}>
        {/* directionality */}
        <div className={'flex flex-col gap-1'}>
          <Typography variant={'label-semibold'}>Trajet</Typography>
          <div className={'flex flex-row gap-14'}>
            {Object.entries(tripStepTypeMapping).map(([key, value]) => (
              <Checkbox
                label={t(value.label)}
                key={key}
                disabled={disabled}
                checked={filters?.directionalities?.includes(key as TripStepTypeEnum)}
                onChange={() => {
                  const currentDirectionalities = filters.directionalities
                  const directionality = key as TripStepTypeEnum

                  setFilters(
                    'directionalities',
                    currentDirectionalities.includes(directionality)
                      ? currentDirectionalities.filter((d) => d !== directionality)
                      : [...currentDirectionalities, directionality]
                  )
                }}
              />
            ))}
          </div>
        </div>

        {/* main trips */}
        <div className={'flex flex-col gap-1'}>
          <Typography variant={'label-semibold'}>Voyages principaux</Typography>
          {mainTrips.length > 0 ? (
            <div className={'p-1 border border-grey-light rounded'}>
              <TripBadge trips={mainTrips} type={TripTypeEnum.PRIMARY} />
            </div>
          ) : (
            '-'
          )}
        </div>

        {/* secondary trips */}
        <div className={'flex flex-col gap-1'}>
          <Typography variant={'label-semibold'}>Voyages secondaires</Typography>
          {secondaryTrips.length > 0 ? (
            <TripBadge trips={secondaryTrips} type={TripTypeEnum.SECONDARY} />
          ) : (
            '-'
          )}
        </div>
      </div>
    </div>
  )
}
